import React, {useEffect, useState, useRef, useMemo} from 'react';
import cx from 'classnames'
import {InView} from 'react-intersection-observer'

/* Utils */
import ProductCardSf from '@sf/product-card'

import {getBrowserCookie} from '../../../../../client/common/persistence/browserCookieUtils';
import {TENANT} from '../../../../modules/account/accountConstants';
import hydraHomeLazyRequest from '../../../../modules/home/hydraHomeLazyRequest'
import {yieldToMain} from '../../../../utils/performance'
import {generateProductStatus,getProductCardDetail} from '../../../../modules/home/homeUtils'
import hbusApiClient from '../../../../modules/analytics/hbus/hbusApiClient';
import leoProductEvents from '../../../../events/leo/productEvents';
import {FEATURE_TOGGLE_NAMES} from '../../../../modules/feature-toggle/featureToggleConstants'

/* Constants */
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'

/* Components */
import ProductCardPwa from './ProductCard'
import RecommendationSkeleton from './RecommendationSkeleton'

/* Styles */
import s from './recomendation.scss'

const Recommendation = (props) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [activeItemCount, setActiveItemCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const attachedEvents = useRef(new Set());
  const [viewedItems, setViewedItems] = useState([])
  const pageFeatureToggleMap = props?.pageFeatureToggleMap

  const [data, setData] = useState(props?.data)
  const {requestParams, componentKey, url, platform} = props
  const ITEMS_PER_PAGE = 6
  const ITEM_WIDTH = 200
  const ITEM_GAP = 24

  const isNewProductCardDesign = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.IS_NEW_CARDS_ON_HOMEPAGE_RECOMMENDATION);

  const RECO_WIDTH = ITEM_WIDTH * ITEMS_PER_PAGE + ITEM_GAP * ITEMS_PER_PAGE

  const roundUp = (num) => Math.ceil(num)
  const totalPageCount = roundUp(data?.products?.length / ITEMS_PER_PAGE)
  const remainingItems = data?.products?.length % ITEMS_PER_PAGE

  const viewedCards = data?.products?.slice(activeSlideIndex * ITEMS_PER_PAGE, (activeSlideIndex + 1) * ITEMS_PER_PAGE)

  const swipeRight = async() => {
    if (activeSlideIndex + 1 === totalPageCount) {
      setActiveItemCount(0)
      setActiveSlideIndex(0)
      return
    }
    await yieldToMain()
    if (activeSlideIndex + 1 === totalPageCount - 1) {
      if (remainingItems === 0) {
        setActiveItemCount(remainingItems)
        setActiveSlideIndex(activeSlideIndex + 1)
        return
      }
      setActiveItemCount(ITEMS_PER_PAGE - remainingItems)
      setActiveSlideIndex(activeSlideIndex + 1)
      return
    }
    await yieldToMain()
    setActiveItemCount(0)
    setActiveSlideIndex(activeSlideIndex + 1)
  }

  const swipeLeft = async() => {
    if (activeSlideIndex === 0) {
      return
    }
    await yieldToMain()
    setActiveItemCount(0)
    setActiveSlideIndex(activeSlideIndex - 1)
  }

  const calculateRecommendationWidth = () => {
    if (remainingItems === 0) {
      return -RECO_WIDTH * activeSlideIndex
    } else {
      if (activeSlideIndex + 1 === totalPageCount) {
        return -RECO_WIDTH * activeSlideIndex + activeItemCount * (ITEM_WIDTH + ITEM_GAP)
      } else {
        return -RECO_WIDTH * activeSlideIndex
      }
    }
  }

  const isVodafone = useMemo(() => getBrowserCookie(TENANT) === 'vodafone' || false, [])

  useEffect(() => {
    if (url && !data) {
      setLoading(true)
      dispatchLazyRequest().then((res) => {
        setLoading(false)
        setData(res?.data?.result?.[0]?.data)
      }).
        catch(() => {
          setLoading(false)
          setError(true)
        })
    }
    if (url && data?.products?.length === 0) {
      setLoading(true)
    }
  }, [])

  useEffect(() => {
    if (viewedCards?.length > 0) {
      const newCards = viewedCards.filter(card => !attachedEvents.current.has(card.productId));
      if (newCards.length > 0) {
        setViewedItems(newCards);
        if(activeSlideIndex !== 0) {
          sendViewEvent(newCards)
        }
        newCards.forEach(card => attachedEvents.current.add(card.productId));
      }
    }
  }, [viewedCards])

  const recoInviewTrigger = () => {
    sendViewEvent(viewedItems)
  }

  const sendViewEvent = (viewedItems) => {
    if (viewedItems?.length > 0) {
      hbusApiClient?.sendEventRequest(DAVINCI_EVENT_NAMES.RECO_PRODUCTS_VIEW, {
        page_type: window?.hepsiBus?.parsers?.getPageType(),
        page_value: window?.hepsiBus?.parsers?.getPageValue(),
        placement_id: data?.placementId,
        placement_title: data?.title,
        products: viewedItems?.map((item) => ({
          customerReviewCount: item?.customerReviewCount,
          customerReviewScore: item?.customerReviewScore,
          listing_id: item?.listingId || '',
          product_id: item?.productId || '',
          position: data?.products?.findIndex((product) => product.sku === item.sku) + 1,
          placement_id: data?.placementId,
          product_status: generateProductStatus(data),
          price: item?.originalPrice?.value?.toFixed(2),
          sku: item?.sku || '',
          reasonDetails: item?.reasonDetails || '',
          labels: ['incentiveToBuy', 'valueAddedService', 'campaign'].map(labelName => {
            const labels = item?.categorizedLabels?.[labelName]
            const firstLabel = labels?.[0]

            return {
              type: labelName,
              id: firstLabel?.tagName || null,
              displayName: firstLabel?.imageUrl || firstLabel?.text || null
            }
          })
        })),
        recoRatio: '3:4',
        main_product: {},
        reasonCode: data?.reasonCode,
        reasonDetails: data?.reasonDetails,
      })
      leoProductEvents.sendRecoProductListViewEvent(viewedItems)
      if(viewedItems.filter(item => item.isAdProduct).length > 0) {
        hbusApiClient?.sendEventRequest(DAVINCI_EVENT_NAMES.SPONSORED_PRODUCT_VIEW, {
          page_type: window?.hepsiBus?.parsers?.getPageType(),
          page_value: window?.hepsiBus?.parsers?.getPageValue(),
          placement_id: data?.placementId,
          placement_title: data?.title,
          products: viewedItems.filter(item => item.isAdProduct)?.map((item) => ({
            adshowid: item?.adInfo?.adShowId || '',
            adcampaignid: item?.adInfo?.campaignId || '',
            listing_id: item?.listingId || '',
            product_id: item?.productId || '',
            position: data?.products?.findIndex((product) => product.sku === item.sku) + 1,
            product_status: '',
            price: item?.originalPrice?.value?.toFixed(2),
            sku: item?.sku || '',
            labels: ['incentiveToBuy', 'valueAddedService', 'campaign'].map(labelName => {
              const labels = item?.categorizedLabels?.[labelName]
              const firstLabel = labels?.[0]

              return {
                type: labelName,
                id: firstLabel?.tagName || null,
                displayName: firstLabel?.imageUrl || firstLabel?.text || null
              }
            })
          })),
          recoRatio: '3:4',
          main_product: {},
          reasonCode: data?.reasonCode,
          reasonDetails: data?.reasonDetails,
        })
        leoProductEvents.sendSponsoredProductListViewEvent(viewedItems.filter(item => item?.isAdProduct))
      }
    }
  }

  const tumuUrls = {
    'home_page.esfst-web-rank1': 'https://www.hepsiburada.com/kampanyalar/en-iyi-taksitli-fiyat',
    'home_page.sfst-web-rank1': 'https://www.hepsiburada.com/dv/super-fiyat-super-teklif',
    'eaf_home_page.web-rank1': 'https://www.hepsiburada.com/kampanyalar/en-avantajli-fiyatlar',
  }

  const isTumuVisible = () => {
    const placementIds = ['home_page.esfst-web-rank1', 'home_page.sfst-web-rank1', 'eaf_home_page.web-rank1']
    return placementIds.includes(data?.placementId)
  }

  const dispatchLazyRequest = () => {
    return hydraHomeLazyRequest({
      componentKey: componentKey,
      platform: platform,
      ...requestParams
    })
  }

  if (loading && !data) {
    return <RecommendationSkeleton/>
  }

  if(error) {
    return null
  }

  return (
    <InView
      as='div'
      onChange={() => recoInviewTrigger()}
      triggerOnce
      className={cx(s.recommendation, {[s.sfCard]: isNewProductCardDesign})}
      data-test-id={props?.type}>
      {!loading && (
        <>
          <div className={s.titleWrapper}>
            <h3 className={s.recoTitle} data-test-id={`${props?.type}-title`}>{data?.title}</h3>
            {isTumuVisible() &&
            <a
              href={tumuUrls[data?.placementId]}
              className={s.recoAll}>
              <span>{'Tümü'}</span>
            </a>}
          </div>
          {activeSlideIndex !== 0 &&
          <i
            onClick={swipeLeft}
            data-test-id={`${props?.type}-left-arrow`}
            role='button' tabIndex='0' aria-label='Önceki ürün grubu'
            aria-describedby='önceki 6 lı ürün grubunu görüntüler'
            className={cx(s.iconLeft, {[s.disabled]: activeSlideIndex === 0})}/>}
          <div className={s.recoWrapper}>
            <div
              className={s.productRow}
              style={{transform: `translateX(${calculateRecommendationWidth()}px)`}}>
              {
                data?.products?.map((item, index) => {
                  const product = isNewProductCardDesign ? getProductCardDetail(item) : null;

                  return isNewProductCardDesign ? (
                    <ProductCardSf
                      dataTestId={`${props?.type}-productbox-${index}`}
                      ariaHidden={viewedCards.includes(item) ? false : true}
                      id={`product-${item.productId}`}
                      title={data?.title}
                      placementId={data?.placementId}
                      reasonCodeMain={data?.reasonCode}
                      reasonDetailsMain={data?.reasonDetails}
                      key={item.productId}
                      isBot={props?.isBot}
                      staticWidth={'220px'}
                      productCardType={'comfort'}
                      isBlankHref={!isVodafone}
                      {...item}
                      {...product} />
                  ) : (
                    <ProductCardPwa
                      dataTestId={`${props?.type}-productbox-${index}`}
                      ariaHidden={viewedCards.includes(item) ? false : true}
                      id={`product-${item.productId}`}
                      title={data?.title}
                      placementId={data?.placementId}
                      reasonCodeMain={data?.reasonCode}
                      reasonDetailsMain={data?.reasonDetails}
                      key={item.productId}
                      index={index}
                      product={item}
                      isBot={props?.isBot}
                      {...item} />
                  );
                })
              }
            </div>
          </div>
          {data?.products?.length >= ITEMS_PER_PAGE && <i
            onClick={swipeRight}
            data-test-id={`${props?.type}-right-arrow`}
            role='button' tabIndex='0' aria-label='Sonraki ürün grubu'
            aria-describedby='sonraki 6 lı ürün grubunu görüntüler'
            className={cx(s.iconRight, {
              [s.disabled]:
              activeSlideIndex + 1 === Math.ceil(data?.products?.length / ITEMS_PER_PAGE)
            })}/>}
        </>
      )}
    </InView>
  )
}

export default Recommendation
